/**
 * ReelsPlaybackController — single source of truth for "which reel plays".
 *
 * Every mounted ReelPlayer registers its <video> element by id. The controller
 * enforces the rule: ONE reel plays at any moment. When `setActive(id)` is
 * called, every other video is paused (and its audio stopped) before the target
 * reel's play() is invoked. Rapid swipes are safe — a monotonically increasing
 * `playSeq` ignores stale play-promise resolutions.
 */
const videos = new Map();       // id -> HTMLVideoElement
let activeId = null;
let mutedPref = false;          // user's audio preference (default: audio on)
let playSeq = 0;                // increments on every setActive — race guard

function pauseVideo(v) {
    if (!v) return;
    try { v.pause(); } catch { /* ignore */ }
    // Explicitly mute the paused one so any lingering audio buffer stops
    try { v.muted = true; } catch { /* ignore */ }
}

function pauseAllExcept(id) {
    videos.forEach((v, vid) => {
        if (vid !== id) pauseVideo(v);
    });
}

export function registerReelVideo(id, el) {
    videos.set(id, el);
}

export function unregisterReelVideo(id) {
    const el = videos.get(id);
    pauseVideo(el);
    videos.delete(id);
    if (activeId === id) activeId = null;
}

export function getActiveReelId() {
    return activeId;
}

export function isMuted() {
    return mutedPref;
}

export function setMuted(m) {
    mutedPref = !!m;
    // Apply to current active only
    const v = activeId ? videos.get(activeId) : null;
    if (v) {
        try { v.muted = mutedPref; } catch { /* ignore */ }
        if (!mutedPref) { v.play().catch(() => {}); }
    }
}

/** Make `id` the sole playing reel. If id is null, everything is paused. */
export async function setActiveReel(id) {
    const mySeq = ++playSeq;
    activeId = id;
    // Pause everything else FIRST
    pauseAllExcept(id);
    if (!id) return;
    const v = videos.get(id);
    if (!v) return;
    try {
        v.currentTime = 0;
        v.muted = mutedPref;
        const p = v.play();
        if (p && typeof p.then === "function") {
            await p.catch(async () => {
                // Autoplay blocked with audio → retry muted (no UI hint per spec)
                if (mySeq !== playSeq) return;
                try {
                    v.muted = true;
                    await v.play();
                } catch { /* ignore */ }
            });
        }
        // If a newer setActive fired while we were playing, pause this one now
        if (mySeq !== playSeq && activeId !== id) pauseVideo(v);
    } catch { /* ignore */ }
}

/** Global cleanup — pause and forget all videos. Call on unmount of feed. */
export function stopAllReels() {
    videos.forEach((v) => pauseVideo(v));
    activeId = null;
    playSeq++;
}
